import React, { useState } from 'react';
import { Typography, Card } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { primaryShades, variantTokens, greyScaleTokens } from '../../../theme/theme';
import { Line } from '@ant-design/plots';
import CustomTooltip from '../Modals/CustomTooltip';

const { Text } = Typography;

// Costanti dei colori
const PRIMARY_300 = primaryShades.primary300;
const WHITE = variantTokens.white;
const GREY_200 = greyScaleTokens.grey200;
const SHADOW_COLOR = 'rgba(0,0,0,0.05)';
const TOOLTIP_SHADOW_COLOR = 'rgba(0,0,0,0.2)';

// Componente per la riga di bottoni superiori
const UserSummary = ({ data, activeTab, onTabChange }) => {
    return (
        <div style={{
            display: 'flex',
            marginBottom: 'clamp(0.625rem, 3vw, 1.25rem)',
            width: '100%',
            borderRadius: 'clamp(0.25rem, 2vw, 0.5rem)',
            overflow: 'hidden',
            boxShadow: `clamp(0rem, 0.1vw, 0rem) clamp(0.0625rem, 0.5vw, 0.125rem) clamp(0.3125rem, 2.5vw, 0.625rem) ${SHADOW_COLOR}`
        }}>
            {data.map((item, index) => {
                const isActive = activeTab === item.key;
                const isFirst = index === 0;
                const isLast = index === data.length - 1;

                return (
                    <div
                        key={item.key}
                        onClick={() => onTabChange(item.key)}
                        style={{
                            flex: 1,
                            padding: 'clamp(0.9375rem, 4vw, 1.875rem) clamp(0.625rem, 3vw, 1.25rem)',
                            cursor: 'pointer',
                            backgroundColor: isActive ? PRIMARY_300 : WHITE,
                            color: isActive ? WHITE : PRIMARY_300,
                            borderRight: isLast ? 'none' : `clamp(0.0625rem, 0.2vw, 0.0625rem) solid ${GREY_200}`,
                            border: `clamp(0.0625rem, 0.2vw, 0.0625rem) solid ${GREY_200}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            textAlign: 'center',
                            borderTopLeftRadius: isFirst ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                            borderBottomLeftRadius: isFirst ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                            borderTopRightRadius: isLast ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                            borderBottomRightRadius: isLast ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                        }}
                    >
                        <div style={{
                            fontSize: 'clamp(1.5rem, 5vw, 2.25rem)',
                            fontWeight: 'bold',
                            color: isActive ? WHITE : PRIMARY_300
                        }}>
                            {item.count}
                        </div>
                        <div style={{
                            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                            fontWeight: 'bold',
                            marginTop: 'clamp(0.15625rem, 1vw, 0.3125rem)',
                            color: isActive ? WHITE : PRIMARY_300
                        }}>
                            {item.label}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Componente per l'intestazione del grafico
const ChartHeader = ({ title, activeTab }) => {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'clamp(0.5rem, 2vw, 1rem)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Text strong>{title}</Text>
                <CustomTooltip title="User trend over time">
                    <InfoCircleOutlined style={{ marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)' }} />
                </CustomTooltip>
            </div>
        </div>
    );
};

// Componente per il grafico lineare
const LineChart = ({ data, activeTab }) => {
    // Dati per il grafico
    const chartData = [];

    for (let i = 0; i < data.labels.length; i++) {
        chartData.push({
            date: data.labels[i],
            value: data.values[i]
        });
    }

    const config = {
        data: chartData,
        xField: 'date',
        yField: 'value',

        smooth: true,
        isStack: false,
        shape: 'smooth',
        connectNulls: true,

        // Applicazione diretta dello stile
        style: {
            stroke: PRIMARY_300,
            lineWidth: 2,
            lineDash: [],
        },

        // Area smussata che segue la linea
        area: {
            smooth: true,
            shape: 'smooth',
            style: {
                fill: 'l(90) 0:rgba(94, 119, 171, 0.01) 0.3:rgba(94, 119, 171, 0.15) 1:rgba(94, 119, 171, 0.25)',
                stroke: 'transparent',
                lineWidth: 0,
            },
        },

        point: {
            size: 3,
            shape: 'circle',
            style: {
                r: 3,
                fill: PRIMARY_300,
                stroke: PRIMARY_300,
                lineWidth: 2,
            },
        },

        // Tooltip ultra minimalista
        tooltip: {
            domStyles: {
                'g2-tooltip': {
                    padding: 'clamp(0.15625rem, 1vw, 0.3125rem) clamp(0.25rem, 1.5vw, 0.5rem)',
                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                    fontWeight: 'bold',
                    backgroundColor: PRIMARY_300,
                    color: WHITE,
                    borderRadius: 'clamp(0.125rem, 1vw, 0.25rem)',
                    boxShadow: `clamp(0rem, 0.1vw, 0rem) clamp(0.0625rem, 0.5vw, 0.125rem) clamp(0.1875rem, 1.5vw, 0.375rem) ${TOOLTIP_SHADOW_COLOR}`,
                    border: 'none'
                },
            },
            showTitle: false,
            showMarkers: true,
            showCrosshairs: false,
        },

        // Torniamo a una configurazione più semplice per l'asse X
        xAxis: {
            label: {
                autoRotate: false,
                rotate: 0,
                offset: 15,
                formatter: (text) => {
                    // Semplificare ulteriormente
                    const parts = text.split('/');
                    if (parts.length > 1) {
                        return parts[0] + '/' + parts[1].substring(0, 3); // Es: 20/Feb
                    }
                    return text;
                },
                style: {
                    fontSize: 11,
                    fill: '#666',
                    textBaseline: 'middle',
                },
            },
            // Mostrare meno etichette
            tickCount: 4,
            // Stile migliorato
            line: {
                style: {
                    stroke: '#eee',
                    lineWidth: 1,
                },
            },
        },

        yAxis: {
            grid: {
                line: {
                    style: {
                        stroke: '#eee',
                    },
                },
            },
        },

        // Aggiungere configurazione per forzare layout
        limitInPlot: false,
        padding: [
            clamp(20, 4, 30), // top
            clamp(10, 2, 20), // right
            clamp(40, 6, 60), // bottom
            clamp(10, 2, 20)  // left
        ], // top, right, bottom, left
    };

    // Helper function for clamp values in the configuration
    function clamp(min, mid, max) {
        return `clamp(${min / 16}rem, ${mid}vw, ${max / 16}rem)`;
    }

    return (
        <Card
            styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)', height: 'clamp(18.75rem, 50vh, 25rem)' } }}
            style={{ height: 'clamp(18.75rem, 50vh, 25rem)' }}
        >
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <ChartHeader title={`${activeTab} - Overview`} activeTab={activeTab} />
                <div style={{ flexGrow: 1, position: 'relative', minHeight: '0' }}>
                    <Line {...config} height={320} />
                </div>
            </div>
        </Card>
    );
};

const LinearChartBox = () => {
    // Dati statistici per i bottoni
    const tabsData = [
        { key: 'USER', label: 'USER', count: 3851 },
        { key: 'SESSION', label: 'SESSION', count: 7059 },
        { key: 'NEW USER', label: 'NEW USER', count: 3258 },
        { key: 'AVG TIME', label: 'AVG TIME', count: '0.39' },
        { key: 'AVG ENG TIME', label: 'AVG ENG TIME', count: '1.11' },
        { key: 'AVG S/U', label: 'AVG S/U', count: '1.83' }
    ];

    // Dati di esempio per il grafico a linee
    const chartsData = {
        'USER': {
            labels: ['20/Feb/2025', '21/Feb/2025', '22/Feb/2025', '23/Feb/2025', '24/Feb/2025', '25/Feb/2025', '26/Feb/2025', '27/Feb/2025'],
            values: [40, 28, 18, 16, 20, 15, 11, 26, 13]
        },
        'SESSION': {
            labels: ['20/Feb/2025', '21/Feb/2025', '22/Feb/2025', '23/Feb/2025', '24/Feb/2025', '25/Feb/2025', '26/Feb/2025', '27/Feb/2025'],
            values: [80, 65, 55, 60, 70, 50, 45, 55]
        },
        'NEW USER': {
            labels: ['20/Feb/2025', '21/Feb/2025', '22/Feb/2025', '23/Feb/2025', '24/Feb/2025', '25/Feb/2025', '26/Feb/2025', '27/Feb/2025'],
            values: [35, 25, 15, 18, 22, 12, 8, 10]
        },
        'AVG TIME': {
            labels: ['20/Feb/2025', '21/Feb/2025', '22/Feb/2025', '23/Feb/2025', '24/Feb/2025', '25/Feb/2025', '26/Feb/2025', '27/Feb/2025'],
            values: [0.5, 0.4, 0.3, 0.35, 0.45, 0.38, 0.25, 0.35]
        },
        'AVG ENG TIME': {
            labels: ['20/Feb/2025', '21/Feb/2025', '22/Feb/2025', '23/Feb/2025', '24/Feb/2025', '25/Feb/2025', '26/Feb/2025', '27/Feb/2025'],
            values: [1.3, 1.1, 0.9, 1.0, 1.2, 1.15, 0.95, 1.05]
        },
        'AVG S/U': {
            labels: ['20/Feb/2025', '21/Feb/2025', '22/Feb/2025', '23/Feb/2025', '24/Feb/2025', '25/Feb/2025', '26/Feb/2025', '27/Feb/2025'],
            values: [2.0, 1.8, 1.6, 1.7, 1.9, 1.75, 1.65, 1.85]
        }
    };

    // Stato per il tab attivo
    const [activeTab, setActiveTab] = useState(tabsData[0].key);

    // Funzione per cambiare tab
    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
    };

    return (
        <div>
            {/* Bottoni superiori */}
            <UserSummary
                data={tabsData}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />

            {/* Grafico lineare */}
            <LineChart
                data={chartsData[activeTab]}
                activeTab={activeTab}
            />
        </div>
    );
};

export default LinearChartBox;
